import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Form,
  PageHeader,
  Popconfirm,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  PauseOutlined,
  ReloadOutlined,
  SendOutlined,
  StopOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";

import { broadcastsApi, whatsappTemplatesApi } from "../../api/broadcastsApi";
import RecipientTable from "../../components/business/broadcasts/RecipientTable";
import { errorMessage } from "../../components/business/tenancy/useOperators";
import {
  EXCLUDE_LABELS,
  FILE_PROBLEM_REASONS,
  IBroadcast,
  IDeliveryStatus,
  IRendered,
  IWhatsAppTemplate,
  STATUS_COLORS,
  TEMPLATE_STATUS_COLORS,
} from "../../types/models/broadcasts";

/**
 * One broadcast: review the list, choose the template, test it, send it.
 *
 * The order of the page is the order of the job, and the send button is last
 * on purpose. Everything above it exists to make the moment of pressing it
 * boring — the counts, the filled-in message, and a test to your own number.
 */

const Container = styled.div`
  /*
   * The app shell gives every page a bounded box and nothing else: the outer
   * Layout is 100vh and antd's Content is flex:auto with min-height:0, so it
   * has a definite height — but App.tsx sets overflow:visible on it inline,
   * which beats the overflow:scroll on its class. Anything taller than the
   * viewport then spills out and is clipped by .App { overflow: hidden }
   * instead of scrolling.
   *
   * So a page scrolls itself, and height:100% is the load-bearing half —
   * overflow-y alone has nothing to overflow against.
   */
  height: 100%;
  overflow-y: auto;
  padding-bottom: 32px;

  .muted {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }

  .var-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  .var-key {
    font-family: monospace;
    min-width: 120px;
    color: rgba(0, 0, 0, 0.65);
  }

  .sample {
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
    font-size: 13px;
  }

  .sample-text {
    white-space: pre-wrap;
    margin-top: 4px;
  }

  .tpl-option-body {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
    white-space: normal;
  }
`;

/** Recipient attributes a variable may be filled from. Mirrors the server's
 *  `comms.broadcasts.RECIPIENT_SOURCES`, which rejects anything else. */
const RECIPIENT_SOURCES = [
  { value: "greeting", label: "Greeting (cleaned first name)" },
  { value: "name_raw", label: "Name exactly as in the file" },
  { value: "phone_e164", label: "Phone number" },
];

/** Refresh cadence while a run is in flight. The work is server-side and a
 *  few hundred messages take a couple of minutes, so this is about watching
 *  progress, not about being live. */
const POLL_MS = 3000;

function BroadcastDetail() {
  const { broadcast_id } = useParams();
  const id = Number(broadcast_id);
  const navigate = useNavigate();

  const [broadcast, setBroadcast] = useState<IBroadcast | null>(null);
  const [templates, setTemplates] = useState<IWhatsAppTemplate[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [rendered, setRendered] = useState<IRendered | null>(null);
  const [delivery, setDelivery] = useState<IDeliveryStatus | null>(null);
  const [templateId, setTemplateId] = useState<number | undefined>();
  const [variableMap, setVariableMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    (quiet = false) => {
      if (!quiet) setLoading(true);
      return broadcastsApi
        .get(id)
        .then((data) => {
          setBroadcast(data);
          // Only seed the form from the server on a full load. Doing it on
          // every poll would yank a half-finished mapping out from under
          // whoever is typing it.
          if (!quiet) {
            setTemplateId(data.template ?? undefined);
            setVariableMap(data.variable_map ?? {});
          }
          return data;
        })
        .catch(() => message.error("Could not load that broadcast."))
        .finally(() => setLoading(false));
    },
    [id]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    broadcastsApi.deliveryStatus().then(setDelivery).catch(() => setDelivery(null));
  }, []);

  /**
   * Templates for *this broadcast's* operator.
   *
   * Scoped rather than fetched wholesale because platform staff can see every
   * tenant: an unfiltered picker would offer one operator's approved
   * templates for another operator's contact list, which the server now
   * rejects anyway.
   */
  const operator = broadcast?.operator;
  useEffect(() => {
    if (!operator) return;
    whatsappTemplatesApi
      .list({ operator })
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, [operator]);

  /**
   * The CSV's headers, read off a stored row. The whole original row is kept
   * on every recipient, which is what makes it possible to map a variable to a
   * column nobody thought about at import time without going back for the file.
   */
  useEffect(() => {
    broadcastsApi
      .recipients({ broadcast: id, page_size: 1 })
      .then((data) => setColumns(Object.keys(data.results[0]?.source_row ?? {})))
      .catch(() => setColumns([]));
  }, [id]);

  const refreshRendered = useCallback(() => {
    broadcastsApi.rendered(id).then(setRendered).catch(() => setRendered(null));
  }, [id]);

  useEffect(() => {
    if (broadcast?.template) refreshRendered();
  }, [broadcast?.template, broadcast?.variable_map, refreshRendered]);

  // Poll only while there is something to watch.
  useEffect(() => {
    if (broadcast?.status !== "sending") return;
    const timer = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(timer);
  }, [broadcast?.status, load]);

  const chosen = useMemo(
    () => templates.find((t) => t.id === templateId) ?? broadcast?.template_detail ?? null,
    [templates, templateId, broadcast]
  );

  const sourceOptions = useMemo(
    () => [
      ...RECIPIENT_SOURCES,
      ...columns.map((header) => ({
        value: `column:${header}`,
        label: `Column: ${header}`,
      })),
    ],
    [columns]
  );

  const sync = () => {
    if (!operator) return;
    setSyncing(true);
    // The operator comes from the broadcast rather than being asked for again.
    // Someone who can act for two tenants would otherwise be answering the
    // same question twice on one page, and the server refuses to guess.
    whatsappTemplatesApi
      .sync(operator)
      .then((report) => {
        message.success(
          `${report.total} templates — ${report.added} new, ${report.updated} updated` +
            (report.removed ? `, ${report.removed} no longer in the account` : "")
        );
        return whatsappTemplatesApi.list({ operator }).then(setTemplates);
      })
      .catch((error) => message.error(errorMessage(error, "Could not reach Meta.")))
      .finally(() => setSyncing(false));
  };

  const saveTemplate = () => {
    if (!templateId) {
      message.warning("Choose a template first.");
      return;
    }
    setSaving(true);
    broadcastsApi
      .ready(id, templateId, variableMap)
      .then((data) => {
        setBroadcast(data);
        refreshRendered();
        message.success("Template saved — this list is ready to send.");
      })
      .catch((error) => message.error(errorMessage(error, "That could not be saved.")))
      .finally(() => setSaving(false));
  };

  const act = (call: Promise<IBroadcast>, success: string) => {
    setBusy(true);
    call
      .then((data) => {
        setBroadcast(data);
        message.success(success);
      })
      .catch((error) => message.error(errorMessage(error, "That did not work.")))
      .finally(() => setBusy(false));
  };

  if (loading || !broadcast) return <Container />;

  const { counts, status } = broadcast;
  const problems = FILE_PROBLEM_REASONS.reduce((n, r) => n + (counts[r] ?? 0), 0);
  const done = counts.sent + counts.delivered + counts.read + counts.failed;
  const attempted = done + counts.sendable;
  const anyEmpty = rendered?.samples.some((s) => s.has_empty) ?? false;

  return (
    <Container>
      <PageHeader
        onBack={() => navigate("/broadcasts")}
        title={broadcast.name}
        subTitle={broadcast.source_filename}
        tags={<Tag color={STATUS_COLORS[status]}>{status}</Tag>}
        extra={[
          status === "sending" ? (
            <Button key="pause" icon={<PauseOutlined />} loading={busy}
                    onClick={() => act(broadcastsApi.pause(id), "Paused.")}>
              Pause
            </Button>
          ) : null,
          status === "sending" || status === "paused" ? (
            <Popconfirm
              key="cancel"
              title={
                <div style={{ maxWidth: 260 }}>
                  Stop this broadcast for good?
                  <div className="muted">
                    Contacts already sent to stay sent — there is no unsending.
                  </div>
                </div>
              }
              okText="Stop it"
              onConfirm={() => act(broadcastsApi.cancel(id), "Stopped.")}
            >
              <Button danger icon={<StopOutlined />} loading={busy}>
                Stop
              </Button>
            </Popconfirm>
          ) : null,
          status === "ready" || status === "paused" ? (
            <Popconfirm
              key="send"
              title={
                <div style={{ maxWidth: 280 }}>
                  Send to {counts.sendable} contacts?
                  <div className="muted">
                    {anyEmpty
                      ? "A variable is empty for some contacts — Meta rejects empty values."
                      : "Messages go out one at a time, with a pause between them."}
                  </div>
                </div>
              }
              okText="Send"
              onConfirm={() =>
                act(broadcastsApi.send(id), "Sending — this runs in the background.")
              }
            >
              <Button type="primary" icon={<SendOutlined />} loading={busy}>
                {status === "paused" ? "Resume" : `Send to ${counts.sendable}`}
              </Button>
            </Popconfirm>
          ) : null,
        ].filter(Boolean)}
      />

      <Row gutter={16} style={{ padding: "0 24px 16px" }}>
        <Col span={4}>
          <Statistic title="Rows in the file" value={counts.total} />
        </Col>
        <Col span={4}>
          <Statistic title="To send" value={counts.sendable}
                     valueStyle={{ color: "#3f8600" }} />
        </Col>
        <Col span={4}>
          <Statistic title="Can’t be sent to" value={problems}
                     valueStyle={problems ? { color: "#cf1322" } : undefined} />
        </Col>
        <Col span={4}>
          <Statistic title="Accepted" value={counts.sent} />
        </Col>
        <Col span={4}>
          <Statistic title="Delivered" value={counts.delivered + counts.read} />
        </Col>
        <Col span={4}>
          <Statistic title="Failed" value={counts.failed}
                     valueStyle={counts.failed ? { color: "#cf1322" } : undefined} />
        </Col>
      </Row>

      {done > 0 && (
        <div style={{ padding: "0 24px 16px" }}>
          <Progress
            percent={Math.round((done / Math.max(attempted, 1)) * 100)}
            status={status === "sending" ? "active" : counts.failed ? "exception" : undefined}
            format={() => `${done} of ${attempted}`}
          />
        </div>
      )}

      {/*
        "Accepted" and "Delivered" are different claims, and without a verified
        webhook only the first is knowable. Saying so is the difference between
        a delivery report and one that quietly means something else.
      */}
      {delivery && !delivery.signature_verification && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ margin: "0 24px 16px" }}
          message="Delivery receipts are not verified"
          description={
            delivery.subscription_configured
              ? "No WhatsApp app secret is configured, so callbacks from Meta cannot be " +
                "checked and anyone who finds the webhook URL could write to this log. " +
                "Accepted counts are reliable; delivered and read are not."
              : "No webhook is configured, so this can only report what was accepted by " +
                "Meta — not what reached a handset."
          }
        />
      )}

      {problems > 0 && broadcast.is_editable && (
        <Alert
          type="info"
          showIcon
          style={{ margin: "0 24px 16px" }}
          message={`${problems} rows can’t be sent to`}
          description={
            <>
              {FILE_PROBLEM_REASONS.filter((r) => counts[r] > 0).map((r) => (
                <Tag key={r}>
                  {EXCLUDE_LABELS[r]}: {counts[r]}
                </Tag>
              ))}
              <div style={{ marginTop: 8 }}>
                Open the matching tab below to fix a number by hand — a corrected
                one is re-checked and goes back into the send.
              </div>
            </>
          }
        />
      )}

      <Row gutter={16} style={{ padding: "0 24px 16px" }}>
        <Col span={12}>
          <Card
            title="Template"
            size="small"
            extra={
              <Space>
                <Tooltip title="Pull the latest template list from Meta">
                  <Button size="small" icon={<ReloadOutlined />} loading={syncing}
                          onClick={sync} />
                </Tooltip>
                <Button type="primary" size="small" loading={saving}
                        disabled={!broadcast.is_editable} onClick={saveTemplate}>
                  Save
                </Button>
              </Space>
            }
          >
            <Form layout="vertical" size="small">
              <Form.Item label="Approved template" required>
                <Select
                  value={templateId}
                  disabled={!broadcast.is_editable}
                  onChange={(value) => {
                    setTemplateId(value);
                    // The keys belong to the old template and would be
                    // rejected against the new one — a positional "1" against
                    // a named template is the common version of this.
                    setVariableMap({});
                  }}
                  placeholder="Choose a template"
                  optionLabelProp="label"
                  listHeight={320}
                  options={templates.map((t) => ({
                    value: t.id,
                    label: `${t.name} · ${t.language}`,
                    disabled: !t.is_sendable,
                    title: t.name,
                  }))}
                />
              </Form.Item>
            </Form>

            {chosen && (
              <>
                <Space size={4} style={{ marginBottom: 8 }} wrap>
                  <Tag color={TEMPLATE_STATUS_COLORS[chosen.status]}>
                    {chosen.status}
                  </Tag>
                  <Tag>{chosen.language}</Tag>
                  <Tag>{chosen.category}</Tag>
                  {/* Named vs positional decides the wire shape of every
                      parameter, so it is worth being visible. */}
                  <Tag color="purple">{chosen.parameter_format}</Tag>
                </Space>

                <div className="sample-text" style={{ marginBottom: 12 }}>
                  {chosen.preview}
                </div>

                {chosen.button_labels.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    {chosen.button_labels.map((label) => (
                      <Tag key={label} color="blue">
                        {label}
                      </Tag>
                    ))}
                  </div>
                )}

                {chosen.variables.length === 0 ? (
                  <div className="muted">
                    This template takes no variables — everybody gets the same text.
                  </div>
                ) : (
                  chosen.variables.map((key) => (
                    <div className="var-row" key={key}>
                      <span className="var-key">{`{{${key}}}`}</span>
                      <Select
                        size="small"
                        style={{ flex: 1 }}
                        value={variableMap[key] || undefined}
                        disabled={!broadcast.is_editable}
                        placeholder="Fill it from…"
                        options={sourceOptions}
                        onChange={(value) =>
                          setVariableMap((current) => ({ ...current, [key]: value }))
                        }
                      />
                    </div>
                  ))
                )}
              </>
            )}
          </Card>
        </Col>

        <Col span={12}>
          <Card title="What they’ll receive" size="small">
            {!rendered || rendered.samples.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Save a template to see it filled in with real contacts"
              />
            ) : (
              <>
                {anyEmpty && (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 8 }}
                    message="A variable is empty for some contacts"
                    description="Meta rejects an empty value, so the run would fail on the first one."
                  />
                )}
                <div className="muted">
                  {rendered.template_name} · {rendered.template_language} ·{" "}
                  {rendered.parameter_format}
                </div>
                {rendered.samples.map((sample) => (
                  <div className="sample" key={sample.recipient}>
                    <span className="muted">{sample.phone_e164}</span>
                    <div className="sample-text">{sample.text}</div>
                  </div>
                ))}
              </>
            )}
          </Card>
        </Col>
      </Row>

      <div style={{ padding: "0 24px" }}>
        <RecipientTable
          broadcastId={id}
          counts={counts}
          editable={broadcast.is_editable}
          canTestSend={status === "ready" || status === "paused"}
          onChanged={() => load(true)}
        />
      </div>
    </Container>
  );
}

export default BroadcastDetail;
